// Market Service - API communication for cashback and transaction management

class MarketService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://api.elmo-burns.com/api/v1';
    this._listeners = [];
    this._cashbackData = null;
    this._monitorStatus = null;
    this._lastPayoutCheck = null;
    this._lastPayoutId = null;
    this._payoutPollingInterval = null;
    this._currentPlayer = null;
  }

  // Get player cashback information
  async getPlayerCashback(playerName) {
    try {
      const url = `${this.baseUrl}/market/player/${playerName}/cashback/`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      // Check for payout events
      if (data.payout_event && this._currentPlayer === playerName) {
        // Only trigger if we haven't seen this specific payout before
        const eventTimestamp = data.payout_event.timestamp;
        const payoutId = data.payout_event.tx_hash || `${eventTimestamp}`;
        
        if (!this._lastPayoutCheck || 
            (eventTimestamp > this._lastPayoutCheck && this._lastPayoutId !== payoutId)) {
          this._lastPayoutCheck = eventTimestamp;
          this._lastPayoutId = payoutId;
          console.log('New payout detected:', data.payout_event);
          // Notify listeners about the payout event
          this._notifyPayoutEvent(data.payout_event);
        }
      }
      
      this._cashbackData = data;
      this._currentPlayer = playerName;
      this._notifyListeners();
      return data;
    } catch (error) {
      console.error('Error fetching player cashback:', error);
      throw error;
    }
  }

  // Get player transaction history
  async getPlayerTransactions(playerName, page = 1, limit = 20) {
    try {
      const response = await fetch(
        `${this.baseUrl}/market/player/${playerName}/transactions/?page=${page}&limit=${limit}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching player transactions:', error);
      throw error;
    }
  }

  // Cashback payout functionality removed - will be handled by backend processes

  // Get monitor status
  async getMonitorStatus() {
    try {
      const response = await fetch(`${this.baseUrl}/market/monitor/status/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this._monitorStatus = data;
      this._notifyListeners();
      return data;
    } catch (error) {
      console.error('Error fetching monitor status:', error);
      throw error;
    }
  }

  // Get leaderboard
  async getLeaderboard(period = 'monthly', page = 1, limit = 10) {
    try {
      const response = await fetch(
        `${this.baseUrl}/market/leaderboard/?period=${period}&page=${page}&limit=${limit}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      throw error;
    }
  }

  // Get historical monthly leaderboard
  async getHistoricalMonthlyLeaderboard(year = null, month = null, limit = 100) {
    try {
      const currentDate = new Date();
      const targetYear = year || currentDate.getFullYear();
      const targetMonth = month || (currentDate.getMonth() + 1);
      
      const response = await fetch(
        `${this.baseUrl}/market/leaderboard/historical/?year=${targetYear}&month=${targetMonth}&limit=${limit}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching historical monthly leaderboard:', error);
      throw error;
    }
  }

  // Get monthly bonus distribution data
  async getMonthlyBonusDistribution(year = null, month = null) {
    try {
      let url = `${this.baseUrl}/market/monthly-bonus-distribution/`;
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      if (params.toString()) {
        url += '?' + params.toString();
      }
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching monthly bonus distribution:', error);
      throw error;
    }
  }

  // Utility: Get tier info based on volume
  getTierInfo(totalVolume) {
    const tiers = [
      { name: 'Bronze', min: 0, max: 999.99, rate: 2.5, color: '#CD7F32' },
      { name: 'Silver', min: 1000, max: 4999.99, rate: 2.6, color: '#C0C0C0' },
      { name: 'Gold', min: 5000, max: 19999.99, rate: 2.7, color: '#FFD700' },
      { name: 'Diamond', min: 20000, max: 99999.99, rate: 2.8, color: '#B9F2FF' },
      { name: 'Champion', min: 100000, max: Infinity, rate: 2.9, color: '#FF6B35' }
    ];

    return tiers.find(tier => totalVolume >= tier.min && totalVolume <= tier.max) || tiers[0];
  }

  // Get next tier info
  getNextTierInfo(currentVolume) {
    const tiers = [
      { name: 'Bronze', min: 0, max: 999.99, rate: 2.5 },
      { name: 'Silver', min: 1000, max: 4999.99, rate: 2.6 },
      { name: 'Gold', min: 5000, max: 19999.99, rate: 2.7 },
      { name: 'Diamond', min: 20000, max: 99999.99, rate: 2.8 },
      { name: 'Champion', min: 100000, max: Infinity, rate: 2.9 }
    ];

    const currentTier = tiers.find(tier => currentVolume >= tier.min && currentVolume <= tier.max);
    const currentIndex = tiers.indexOf(currentTier);
    
    if (currentIndex < tiers.length - 1) {
      const nextTier = tiers[currentIndex + 1];
      const needed = nextTier.min - currentVolume;
      return {
        nextTier,
        volumeNeeded: needed,
        progress: currentVolume / nextTier.min
      };
    }
    
    return null; // Already at max tier
  }

  // Start polling for payout events for a specific player
  startPayoutPolling(playerName, intervalMs = 5000) {
    this.stopPayoutPolling(); // Stop any existing polling
    
    // Reset payout tracking when starting polling for a new player
    if (this._currentPlayer !== playerName) {
      this._lastPayoutCheck = null;
      this._lastPayoutId = null;
    }
    
    this._currentPlayer = playerName;
    this._payoutPollingInterval = setInterval(async () => {
      try {
        await this.getPlayerCashback(playerName);
      } catch (error) {
        console.error('Error during payout polling:', error);
      }
    }, intervalMs);
  }

  // Stop payout polling
  stopPayoutPolling() {
    if (this._payoutPollingInterval) {
      clearInterval(this._payoutPollingInterval);
      this._payoutPollingInterval = null;
      this._currentPlayer = null;
      // Reset payout tracking when stopping polling
      this._lastPayoutCheck = null;
      this._lastPayoutId = null;
      console.log('Stopped payout polling and reset payout tracking');
    }
  }

  // Notify listeners about payout events
  _notifyPayoutEvent(payoutEvent) {
    this._listeners.forEach(callback => {
      callback({
        cashbackData: this._cashbackData,
        monitorStatus: this._monitorStatus,
        payoutEvent: payoutEvent
      });
    });
  }

  // Subscribe to data changes
  subscribe(callback) {
    this._listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      this._listeners = this._listeners.filter(listener => listener !== callback);
    };
  }

  // Notify all listeners
  _notifyListeners() {
    this._listeners.forEach(callback => {
      callback({
        cashbackData: this._cashbackData,
        monitorStatus: this._monitorStatus
      });
    });
  }

  // Get current cached data
  getCachedData() {
    return {
      cashbackData: this._cashbackData,
      monitorStatus: this._monitorStatus
    };
  }
}

// Singleton instance
const marketService = new MarketService();
export default marketService;
